import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Checkbox,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApolloClient } from '@apollo/client';
import { useQueryAsUser, useMutationCoordinate } from '../clients/graphql/graphql.hooks';
import {
  userPilotsForOverAllocationQuery,
  balancingDecisionsGetQuery,
  jediResolveOverAllocation,
  OverAllocationSleeve,
} from '../clients/graphql/views/over-allocation-queries';
import { pilotTriggerHistoryQuery } from '../clients/graphql/components/tables-queries';
import _ from 'lodash';

interface BalancingParameters {
  settingParameters?: { slaveAutopilotPortfolioCash?: number };
  brokerParameters?: { fundsAvailable?: number };
  slaveValuation?: number;
}

interface OverAllocationBannerProps {
  userKey: number;
}

type AdjustmentStrategy = 'proportional' | 'minimized';

type SleeveData = {
  autopilotCash: number;
  buyingPower: number;
  marketValue: number;
  equilibriumAchieved: boolean;
};

type SleeveSource = {
  autoPilotSettingsKey: number;
  allocationAmount: number;
  slavePortfolioKey: number;
  slavePortfolio?: {
    portfolioKey: number;
    portfolioName: string;
    brokerName: string;
  };
  masterPortfolio?: {
    autoPilotMasterPortfolio?: {
      title: string;
    };
  };
};

type ComputedSleeve = OverAllocationSleeve & {
  marketValue: number;
  equilibriumAchieved: boolean;
};

type ComputedPortfolio = {
  portfolioKey: number;
  portfolioName: string;
  brokerName: string;
  buyingPower: number;
  totalAutopilotCash: number;
  overAllocation: number;
  sleeves: ComputedSleeve[];
  requiredReduction: number;
};

const STRATEGY_LABELS: Record<AdjustmentStrategy, string> = {
  proportional: 'Proportional',
  minimized: 'Minimized',
};

const STRATEGY_DESCRIPTIONS: Record<AdjustmentStrategy, string> = {
  proportional:
    'Within each stage, reduction is split proportionally by stage capacity (AP cash or position value).',
  minimized:
    'Within each stage, reduction is applied to the largest stage-capacity sleeve first, then next largest.',
};
const COVERAGE_TOLERANCE = 0.01;
const SELL_WARNING_TOLERANCE = 0.02;

export const OverAllocationBanner: React.FC<OverAllocationBannerProps> = ({ userKey }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [adjustments, setAdjustments] = useState<Record<number, number>>({});
  const [customSelected, setCustomSelected] = useState<Record<number, boolean>>({});
  const [strategy, setStrategy] = useState<AdjustmentStrategy>('proportional');
  const [sleeveData, setSleeveData] = useState<Record<number, SleeveData>>({});
  const [loadingSleeveData, setLoadingSleeveData] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [resolveTotal, setResolveTotal] = useState(0);

  const apolloClient = useApolloClient();
  const { data: pilotsData, loading: pilotsLoading } = useQueryAsUser(userPilotsForOverAllocationQuery, {
    userKey,
    skip: !userKey,
  });

  const [resolveOverAllocationMutation] = useMutationCoordinate(jediResolveOverAllocation, {});

  const pilots: SleeveSource[] = useMemo(
    () => pilotsData?.autoPilotSettingsGet?.autoPilotSettings ?? [],
    [pilotsData],
  );

  const refreshSleeveData = useCallback(async () => {
    if (pilots.length === 0) return;
    setLoadingSleeveData(true);

    const coordinate = process.env.REACT_APP_COORDINATE;
    const results: Record<number, SleeveData> = {};
    const promises = pilots.map(async (pilot: SleeveSource) => {
      const autoPilotSettingsKey = pilot.autoPilotSettingsKey;
      const baseHeaders = {
        coordinate: coordinate || '',
        onBehalfOfUserKey: `${userKey || ''}`,
      };
      try {
        const [balancingRes, triggerRes] = await Promise.all([
          apolloClient.query({
            query: balancingDecisionsGetQuery,
            variables: { autoPilotSettingsKey },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
            context: { headers: baseHeaders },
          }),
          apolloClient.query({
            query: pilotTriggerHistoryQuery,
            variables: { userKey, autoPilotSettingsKey, limit: 1, offset: 0 },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
            context: { headers: baseHeaders },
          }),
        ]);

        const params = balancingRes.data?.balancingDecisionsGet?.balancingParameters;
        if (!params) return;

        const parsedRaw = JSON.parse(params);
        const parsed: BalancingParameters =
          parsedRaw && typeof parsedRaw === 'object' ? (parsedRaw as BalancingParameters) : {};
        const autopilotCash = parsed.settingParameters?.slaveAutopilotPortfolioCash ?? 0;
        const buyingPower = parsed.brokerParameters?.fundsAvailable ?? 0;
        const marketValue = parsed.slaveValuation ?? 0;

        const latestTrigger = triggerRes.data?.triggerHistoryGet?.[0];
        const equilibriumAchieved = !!latestTrigger?.equilibriumAchievedAt;

        results[autoPilotSettingsKey] = {
          autopilotCash,
          buyingPower,
          marketValue,
          equilibriumAchieved,
        };
      } catch {
        try {
          const balancingOnly = await apolloClient.query({
            query: balancingDecisionsGetQuery,
            variables: { autoPilotSettingsKey },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
            context: { headers: baseHeaders },
          });
          const params = balancingOnly.data?.balancingDecisionsGet?.balancingParameters;
          if (params) {
            const parsedRaw = JSON.parse(params);
            const parsed: BalancingParameters =
              parsedRaw && typeof parsedRaw === 'object' ? (parsedRaw as BalancingParameters) : {};
            const autopilotCash = parsed.settingParameters?.slaveAutopilotPortfolioCash ?? 0;
            const buyingPower = parsed.brokerParameters?.fundsAvailable ?? 0;
            const marketValue = parsed.slaveValuation ?? 0;
            results[autoPilotSettingsKey] = {
              autopilotCash,
              buyingPower,
              marketValue,
              equilibriumAchieved: false,
            };
          }
        } catch {
          // Skip sleeves that cannot be loaded
        }
      }
    });

    await Promise.all(promises);
    setSleeveData(results);
    setLoadingSleeveData(false);
  }, [apolloClient, pilots, userKey]);

  useEffect(() => {
    refreshSleeveData();
  }, [refreshSleeveData]);

  const defaultAdjustmentsByStrategy = useMemo(() => {
    if (Object.keys(sleeveData).length === 0) return {};
    const byPortfolio = _.groupBy(pilots, (p: SleeveSource) => p.slavePortfolioKey);
    const result: Record<number, number> = {};

    for (const portfolioKeyStr of Object.keys(byPortfolio)) {
      const sleevesList = byPortfolio[portfolioKeyStr];
      let totalAutopilotCash = 0;
      let buyingPower = 0;
      const selectedSleeves = sleevesList
        .filter((s) => {
          const d = sleeveData[s.autoPilotSettingsKey];
          return !!customSelected[s.autoPilotSettingsKey] && (d?.autopilotCash ?? 0) >= 0;
        })
        .map((s) => {
          const d = sleeveData[s.autoPilotSettingsKey];
          return {
            autoPilotSettingsKey: s.autoPilotSettingsKey,
            equilibriumAchieved: !!d?.equilibriumAchieved,
            autopilotCash: d?.autopilotCash ?? 0,
            positionValue: Math.max(0, (d?.marketValue ?? 0) - (d?.autopilotCash ?? 0)),
          };
        });

      for (const s of sleevesList) {
        const data = sleeveData[s.autoPilotSettingsKey];
        if (!data) continue;
        totalAutopilotCash += data.autopilotCash;
        buyingPower = data.buyingPower;
      }

      const requiredReduction = Math.max(0, _.round(totalAutopilotCash - buyingPower, 2));
      if (requiredReduction <= 1 || selectedSleeves.length === 0) continue;

      const localAdjustments: Record<number, number> = {};
      let remaining = requiredReduction;

      const applyStage = (stageCandidates: { autoPilotSettingsKey: number; capacity: number }[]): void => {
        if (remaining <= 0 || stageCandidates.length === 0) return;
        const candidates = stageCandidates.filter((c) => c.capacity > 0);
        if (candidates.length === 0) return;

        const stageCapacity = _.sumBy(candidates, (c) => c.capacity);
        if (stageCapacity <= 0) return;

        if (strategy === 'proportional') {
          const consume = Math.min(remaining, stageCapacity);
          candidates.forEach((c) => {
            const share = _.round((c.capacity / stageCapacity) * consume, 6);
            localAdjustments[c.autoPilotSettingsKey] = (localAdjustments[c.autoPilotSettingsKey] ?? 0) + share;
          });
          remaining = _.round(remaining - consume, 6);
          return;
        }

        const ordered = _.orderBy(candidates, ['capacity'], ['desc']);
        ordered.forEach((c) => {
          if (remaining <= 0) return;
          const take = Math.min(remaining, c.capacity);
          localAdjustments[c.autoPilotSettingsKey] = (localAdjustments[c.autoPilotSettingsKey] ?? 0) + take;
          remaining = _.round(remaining - take, 6);
        });
      };

      // Stage 1: pending by AP cash
      applyStage(
        selectedSleeves
          .filter((s) => !s.equilibriumAchieved)
          .map((s) => ({ autoPilotSettingsKey: s.autoPilotSettingsKey, capacity: s.autopilotCash })),
      );
      // Stage 2: equilibrium by AP cash
      applyStage(
        selectedSleeves
          .filter((s) => s.equilibriumAchieved)
          .map((s) => ({ autoPilotSettingsKey: s.autoPilotSettingsKey, capacity: s.autopilotCash })),
      );
      // Stage 3: pending by position value
      applyStage(
        selectedSleeves
          .filter((s) => !s.equilibriumAchieved)
          .map((s) => ({ autoPilotSettingsKey: s.autoPilotSettingsKey, capacity: s.positionValue })),
      );
      // Stage 4: equilibrium by position value
      applyStage(
        selectedSleeves
          .filter((s) => s.equilibriumAchieved)
          .map((s) => ({ autoPilotSettingsKey: s.autoPilotSettingsKey, capacity: s.positionValue })),
      );

      const roundedLocalAdjustments: Record<number, number> = {};
      Object.entries(localAdjustments).forEach(([apsKey, amount]) => {
        roundedLocalAdjustments[parseInt(apsKey, 10)] = Math.max(0, _.round(amount, 2));
      });

      const roundedTotal = _.round(_.sum(Object.values(roundedLocalAdjustments)), 2);
      let delta = _.round(requiredReduction - roundedTotal, 2);

      // Reconcile penny-level rounding drift so projection is not falsely short.
      if (Math.abs(delta) >= COVERAGE_TOLERANCE) {
        const reconciliationOrder = _.orderBy(
          selectedSleeves,
          [
            (s) => (s.equilibriumAchieved ? 1 : 0), // Pending first
            (s) => s.autopilotCash, // Then larger AP cash first
          ],
          ['asc', 'desc'],
        );

        for (const sleeve of reconciliationOrder) {
          if (delta === 0) break;
          const current = roundedLocalAdjustments[sleeve.autoPilotSettingsKey] ?? 0;
          const maxCapacity = _.round(sleeve.autopilotCash + sleeve.positionValue, 2);

          if (delta > 0) {
            const headroom = _.round(maxCapacity - current, 2);
            if (headroom <= 0) continue;
            const add = Math.min(headroom, delta);
            roundedLocalAdjustments[sleeve.autoPilotSettingsKey] = _.round(current + add, 2);
            delta = _.round(delta - add, 2);
          } else {
            if (current <= 0) continue;
            const reduce = Math.min(current, Math.abs(delta));
            roundedLocalAdjustments[sleeve.autoPilotSettingsKey] = _.round(current - reduce, 2);
            delta = _.round(delta + reduce, 2);
          }
        }
      }

      Object.entries(roundedLocalAdjustments).forEach(([apsKey, amount]) => {
        result[parseInt(apsKey, 10)] = amount;
      });
    }
    return result;
  }, [pilots, sleeveData, strategy, customSelected]);

  const portfolios = useMemo<ComputedPortfolio[]>(() => {
    if (Object.keys(sleeveData).length === 0) return [];
    const byPortfolio = _.groupBy(pilots, (p: SleeveSource) => p.slavePortfolioKey);
    const result: ComputedPortfolio[] = [];

    for (const portfolioKeyStr of Object.keys(byPortfolio)) {
      const sleevesList = byPortfolio[portfolioKeyStr];
      const first = sleevesList[0];
      const portfolioKey = first.slavePortfolio?.portfolioKey ?? parseInt(portfolioKeyStr, 10);
      const portfolioName = first.slavePortfolio?.portfolioName ?? `Portfolio ${portfolioKeyStr}`;
      const brokerName = first.slavePortfolio?.brokerName ?? '';

      let totalAutopilotCash = 0;
      let buyingPower = 0;
      const computedSleeves: ComputedSleeve[] = [];

      sleevesList.forEach((s) => {
        const data = sleeveData[s.autoPilotSettingsKey];
        if (!data) return;
        totalAutopilotCash += data.autopilotCash;
        buyingPower = data.buyingPower;
        const pilotName = s.masterPortfolio?.autoPilotMasterPortfolio?.title ?? `Pilot ${s.autoPilotSettingsKey}`;
        computedSleeves.push({
          autoPilotSettingsKey: s.autoPilotSettingsKey,
          pilotName,
          allocationAmount: s.allocationAmount ?? 0,
          autopilotCash: data.autopilotCash,
          marketValue: data.marketValue,
          equilibriumAchieved: data.equilibriumAchieved,
          suggestedAdjustment: defaultAdjustmentsByStrategy[s.autoPilotSettingsKey] ?? 0,
        });
      });

      const overAllocation = Math.max(0, totalAutopilotCash - buyingPower);
      if (overAllocation <= 1) continue;
      const requiredReduction = Math.max(0, _.round(overAllocation, 2));

      const sortedSleeves = _.orderBy(
        computedSleeves,
        [
          (s: ComputedSleeve) => (s.equilibriumAchieved ? 1 : 0), // pending first
          (s: ComputedSleeve) => s.autopilotCash,
        ],
        ['asc', 'desc'],
      );

      result.push({
        portfolioKey,
        portfolioName,
        brokerName,
        buyingPower,
        totalAutopilotCash: _.round(totalAutopilotCash, 2),
        overAllocation: _.round(overAllocation, 2),
        requiredReduction,
        sleeves: sortedSleeves,
      });
    }
    return result;
  }, [pilots, sleeveData, defaultAdjustmentsByStrategy]);

  const hasOverAllocation = portfolios.some((p) => p.requiredReduction > 1);

  const handleOpenModal = () => {
    setModalOpen(true);
    const initialCustomSelected: Record<number, boolean> = {};
    portfolios.forEach((p) => {
      p.sleeves.forEach((s) => {
        initialCustomSelected[s.autoPilotSettingsKey] = !s.equilibriumAchieved && s.autopilotCash >= 0;
      });
    });
    setCustomSelected(initialCustomSelected);
    setAdjustments(defaultAdjustmentsByStrategy);
  };

  const handleApplyAll = async () => {
    const targetSleeves = portfolios
      .flatMap((p) => p.sleeves)
      .filter((s) => {
        const amount = adjustments[s.autoPilotSettingsKey] ?? s.suggestedAdjustment;
        if (amount <= 0) return false;
        return true;
      });

    if (targetSleeves.length === 0) return;

    try {
      setIsResolving(true);
      setResolvedCount(0);
      setResolveTotal(targetSleeves.length);

      const mutations = targetSleeves.map((s) =>
        resolveOverAllocationMutation({
          variables: {
            autoPilotSettingsKey: s.autoPilotSettingsKey,
          },
        })
          .catch((error) => {
            console.error(`Failed to resolve over-allocation for ${s.autoPilotSettingsKey}:`, error);
          })
          .finally(() => {
          setResolvedCount((prev) => prev + 1);
          }),
      );
      await Promise.allSettled(mutations);
      setModalOpen(false);
      window.location.reload();
    } catch (e) {
      console.error('Failed to resolve over-allocation:', e);
    } finally {
      setIsResolving(false);
      setResolvedCount(0);
      setResolveTotal(0);
    }
  };

  const handleCustomToggle = (autoPilotSettingsKey: number) => {
    const sleeve = portfolios.flatMap((p) => p.sleeves).find((s) => s.autoPilotSettingsKey === autoPilotSettingsKey);
    if (!sleeve || sleeve.autopilotCash < 0) return;

    setCustomSelected((prev) => ({
      ...prev,
      [autoPilotSettingsKey]: !prev[autoPilotSettingsKey],
    }));
  };

  useEffect(() => {
    if (!modalOpen) return;
    setAdjustments(defaultAdjustmentsByStrategy);
  }, [strategy, defaultAdjustmentsByStrategy, modalOpen]);

  if (pilotsLoading || pilots.length === 0) return null;
  const isLoadingBalancing = loadingSleeveData || (Object.keys(sleeveData).length === 0 && pilots.length > 0);
  if (isLoadingBalancing) {
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Checking allocation...
        </Typography>
      </Box>
    );
  }

  if (!hasOverAllocation) return null;
  const totalRequiredReduction = _.round(_.sumBy(portfolios, (p) => p.requiredReduction), 2);
  const projectedAdjustment = _.round(
    _.sumBy(
      portfolios.flatMap((p) => p.sleeves),
      (s) => adjustments[s.autoPilotSettingsKey] ?? s.suggestedAdjustment,
    ),
    2,
  );
  const projectedCoversReduction = projectedAdjustment + COVERAGE_TOLERANCE >= totalRequiredReduction;
  const resolveProgressPct = resolveTotal > 0 ? Math.round((resolvedCount / resolveTotal) * 100) : 0;
  const hasAdjustableTargets = portfolios.some((p) =>
    p.sleeves.some((s) => {
      const amount = adjustments[s.autoPilotSettingsKey] ?? s.suggestedAdjustment;
      return amount > 0;
    }),
  );

  return (
    <>
      <Alert
        severity="warning"
        sx={{ mb: 2, alignItems: 'center' }}
        action={
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={handleOpenModal}
            sx={{ ml: 1, whiteSpace: 'nowrap' }}
          >
            Inspect allocation
          </Button>
        }
      >
        <AlertTitle>Over-allocation detected</AlertTitle>
        The sum of autopilot cash across sleeves exceeds available buying power. Click to inspect and resolve.
      </Alert>

      <Dialog open={modalOpen} onClose={() => (isResolving ? undefined : setModalOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>Resolve over-allocation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is always custom mode. Pending sleeves start selected; you can adjust selection manually.
          </Typography>
          <ToggleButtonGroup
            value={strategy}
            exclusive
            size="small"
            onChange={(_, next: AdjustmentStrategy | null) => {
              if (next) setStrategy(next);
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="proportional">{STRATEGY_LABELS.proportional}</ToggleButton>
            <ToggleButton value="minimized">{STRATEGY_LABELS.minimized}</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Distribution mode: {STRATEGY_LABELS[strategy]}
            </Typography>
            <Tooltip
              placement="right"
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    {STRATEGY_DESCRIPTIONS[strategy]}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Stage order: 1) Pending AP cash 2) Equilibrium AP cash 3) Pending position value 4) Equilibrium
                    position value
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {portfolios.map((portfolio: ComputedPortfolio) => (
            <PortfolioOverAllocationSection
              key={portfolio.portfolioKey}
              portfolio={portfolio}
              adjustments={adjustments}
              customSelected={customSelected}
              onCustomToggle={handleCustomToggle}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 3, py: 2, gap: 1 }}>
          {isResolving && (
            <Box sx={{ minWidth: 240, mr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Resolving allocations... {resolvedCount}/{resolveTotal}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={resolveProgressPct} />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: projectedCoversReduction ? 'success.main' : 'error.main',
              backgroundColor: projectedCoversReduction ? 'success.50' : 'error.50',
              mr: 0.5,
            }}
          >
            {projectedCoversReduction ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <CloseIcon fontSize="small" color="error" />
            )}
            <Typography
              variant="caption"
              sx={{ color: projectedCoversReduction ? 'success.dark' : 'error.dark', fontWeight: 700 }}
            >
              {projectedCoversReduction ? 'Ready to resolve' : 'Insufficient adjustment'}
            </Typography>
            <Typography variant="caption" sx={{ color: projectedCoversReduction ? 'success.dark' : 'error.dark' }}>
              {projectedAdjustment.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} /{' '}
              {totalRequiredReduction.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>
          </Box>
          <Button onClick={() => setModalOpen(false)} disabled={isResolving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyAll}
            disabled={!hasAdjustableTargets || isResolving}
          >
            {isResolving ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface PortfolioOverAllocationSectionProps {
  portfolio: ComputedPortfolio;
  adjustments: Record<number, number>;
  customSelected: Record<number, boolean>;
  onCustomToggle: (autoPilotSettingsKey: number) => void;
}

const PortfolioOverAllocationSection: React.FC<PortfolioOverAllocationSectionProps> = ({
  portfolio,
  adjustments,
  customSelected,
  onCustomToggle,
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      {portfolio.portfolioName} ({portfolio.brokerName})
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Buying power: ${portfolio.buyingPower.toLocaleString()} | Total autopilot cash: $
      {portfolio.totalAutopilotCash.toLocaleString()} | Over-allocation: $
      {portfolio.overAllocation.toLocaleString()}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Remaining reduction target: ${portfolio.requiredReduction.toLocaleString()}
    </Typography>
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">Reduce</TableCell>
            <TableCell>Pilot</TableCell>
            <TableCell align="center">Equilibrium</TableCell>
            <TableCell align="right">Market value</TableCell>
            <TableCell align="right">Allocation</TableCell>
            <TableCell align="right">Autopilot cash</TableCell>
            <TableCell align="right">Target adjustment ($)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {_.orderBy(
            portfolio.sleeves,
            [
              (s: ComputedSleeve) => (s.equilibriumAchieved ? 1 : 0), // Pending first, always
              (s: ComputedSleeve) => s.autopilotCash, // Then AP cash descending
            ],
            ['asc', 'desc'],
          ).map((sleeve) => (
            <TableRow key={sleeve.autoPilotSettingsKey}>
              <TableCell align="center">
                <Checkbox
                  size="small"
                  checked={!!customSelected[sleeve.autoPilotSettingsKey]}
                  disabled={sleeve.autopilotCash < 0}
                  onChange={() => onCustomToggle(sleeve.autoPilotSettingsKey)}
                />
              </TableCell>
              <TableCell>{sleeve.pilotName}</TableCell>
              <TableCell align="center">
                {sleeve.equilibriumAchieved ? (
                  <Chip label="Equilibrium" color="success" size="small" />
                ) : (
                  <Chip label="Pending" color="warning" size="small" />
                )}
              </TableCell>
              <TableCell align="right">${sleeve.marketValue.toLocaleString()}</TableCell>
              <TableCell align="right">${sleeve.allocationAmount.toLocaleString()}</TableCell>
              <TableCell align="right">${sleeve.autopilotCash.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, width: '100%' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {(adjustments[sleeve.autoPilotSettingsKey] ?? sleeve.suggestedAdjustment).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Typography>
                  <Box sx={{ width: 16, display: 'inline-flex', justifyContent: 'center' }}>
                    {(() => {
                      const targetAdjustment = adjustments[sleeve.autoPilotSettingsKey] ?? sleeve.suggestedAdjustment;
                      const sellAmount = _.round(targetAdjustment - sleeve.autopilotCash, 2);
                      const showsSellWarning = sleeve.autopilotCash >= 0 && sellAmount > SELL_WARNING_TOLERANCE;

                      if (!showsSellWarning) return null;

                      return (
                        <Tooltip
                          title={`This will cause ${sellAmount.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })} of positions to be sold`}
                        >
                          <WarningAmberIcon fontSize="small" color="warning" />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);
